import { motion, AnimatePresence } from "framer-motion";
import React, { useEffect, useState } from "react";

const dropdownVariants = {
  open: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
  closed: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    transition: { duration: 0.2 },
  },
};

const itemVariants = {
  open: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
  closed: { opacity: 0, x: -20 },
};

const Dropdown = ({
  id,
  title = "Search",
  items = [],
  onItemSelect,
  disabled = false,
  isOpen,
  onToggle,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handleItemClick = (item) => {
    setSelectedItem(item);
    onToggle(id);
    onItemSelect(item.id || item.uuid || item.value);
  };

  const filteredItems = items.filter(
    (item) =>
      item?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.value?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && !e.target.closest(`#${id}`)) {
        onToggle(id);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, id, onToggle]);

  return (
    <motion.div id={id} className="relative w-full max-w-xs md:max-w-md" layout>
      {/* Dropdown Button */}
      <motion.button
        className="bg-white text-gray-800 font-medium rounded-lg text-sm md:text-base px-4 py-3 md:py-2.5 w-full 
        border border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
        transition-all disabled:cursor-not-allowed flex items-center justify-between"
        type="button"
        onClick={() => onToggle(id)}
        disabled={disabled}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
      >
        <span className="truncate">
          {selectedItem
            ? selectedItem.title || selectedItem.name || selectedItem.value
            : title}
        </span>
        <motion.svg
          className="w-5 h-5 md:w-4 md:h-4 ml-2"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 24 }}
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m1 1 4 4 4-4"
          />
        </motion.svg>
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute z-50 bg-white rounded-lg shadow-lg w-full mt-1 md:mt-2 dark:bg-gray-700 left-0 right-0"
            variants={dropdownVariants}
            initial="closed"
            animate="open"
            exit="closed"
            layout
          >
            <div className="p-2 md:p-3">
              {/* Search Input */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-500 dark:text-gray-400"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 20"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="block w-full p-2 pl-10 text-sm md:text-base text-gray-900 border 
                    border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 
                    dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white 
                    dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </motion.div>
            </div>

            {/* Items List */}
            <motion.ul
              className="max-h-[50vh] md:max-h-48 px-2 md:px-3 pb-2 md:pb-3 overflow-y-auto text-sm md:text-base 
              text-gray-700 dark:text-gray-200"
              initial="closed"
              animate="open"
              variants={{
                open: {
                  transition: { staggerChildren: 0.05, delayChildren: 0.1 },
                },
                closed: { transition: { staggerChildren: 0.05 } },
              }}
            >
              {filteredItems.map((item) => (
                <motion.li
                  key={item.id}
                  className="capitalize"
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div
                    className="flex items-center p-1.5 md:p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 
                    cursor-pointer transition-colors"
                    onClick={() => handleItemClick(item)}
                  >
                    <label className="w-full truncate font-medium text-gray-900 dark:text-gray-300">
                      {item.title || item.name || item.value}
                    </label>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Dropdown;

// import React, { useEffect, useState } from "react";

// const Dropdown = ({ id, title = "Search", items = [], onItemSelect, disabled = false, isOpen, onToggle }) => {
//     const [searchTerm, setSearchTerm] = useState("");
//     const [selectedItem, setSelectedItem] = useState(null);

//     const handleSearch = (e) => setSearchTerm(e.target.value);

//     const handleItemClick = (item) => {
//         setSelectedItem(item);
//         onToggle(id);
//         onItemSelect(item.id || item.uuid || item.value);
//     };

//     const filteredItems = items.filter((item) =>
//         item?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//         item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//         item?.value?.toLowerCase().includes(searchTerm.toLowerCase())
//     );

//     useEffect(() => {
//         const handleClickOutside = (e) => {
//             if (isOpen && !e.target.closest(`#${id}`)) {
//                 onToggle(id);
//             }
//         };

//         document.addEventListener("mousedown", handleClickOutside);
//         return () => document.removeEventListener("mousedown", handleClickOutside);
//     }, [isOpen, id, onToggle]);

//     return (
//         <div id={id} className="relative w-full max-w-xs md:max-w-md">
//             {/* Dropdown Button */}
//             <button
//                 className="bg-white text-gray-800 font-medium rounded-lg text-sm md:text-base px-4 py-3 md:py-2.5 w-full
//                 border border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500
//                 transition-all disabled:cursor-not-allowed flex items-center justify-between"
//                 type="button"
//                 onClick={() => onToggle(id)}
//                 disabled={disabled}
//             >
//                 <span className="truncate">
//                     {selectedItem ? selectedItem.title || selectedItem.name || selectedItem.value : title}
//                 </span>
//                 <svg
//                     className={`w-5 h-5 md:w-4 md:h-4 ml-2 transition-transform ${isOpen ? "rotate-180" : ""}`}
//                     aria-hidden="true"
//                     xmlns="http://www.w3.org/2000/svg"
//                     fill="none"
//                     viewBox="0 0 10 6"
//                 >
//                     <path
//                         stroke="currentColor"
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth="2"
//                         d="m1 1 4 4 4-4"
//                     />
//                 </svg>
//             </button>

//             {/* Dropdown Menu */}
//             {isOpen && (
//                 <div className="absolute z-50 bg-white rounded-lg shadow-lg w-full mt-1 md:mt-2 dark:bg-gray-700
//                 left-0 right-0">
//                     <div className="p-2 md:p-3">
//                         {/* Search Input */}
//                         <div className="relative">
//                             <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
//                                 <svg
//                                     className="w-4 h-4 text-gray-500 dark:text-gray-400"
//                                     aria-hidden="true"
//                                     xmlns="http://www.w3.org/2000/svg"
//                                     fill="none"
//                                     viewBox="0 0 20 20"
//                                 >
//                                     <path
//                                         stroke="currentColor"
//                                         strokeLinecap="round"
//                                         strokeLinejoin="round"
//                                         strokeWidth="2"
//                                         d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
//                                     />
//                                 </svg>
//                             </div>
//                             <input
//                                 type="text"
//                                 className="block w-full p-2 pl-10 text-sm md:text-base text-gray-900 border
//                                 border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500
//                                 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white
//                                 dark:focus:ring-blue-500 dark:focus:border-blue-500"
//                                 placeholder="Search"
//                                 value={searchTerm}
//                                 onChange={handleSearch}
//                             />
//                         </div>
//                     </div>

//                     {/* Items List */}
//                     <ul className="max-h-[50vh] md:max-h-48 px-2 md:px-3 pb-2 md:pb-3 overflow-y-auto text-sm md:text-base
//                     text-gray-700 dark:text-gray-200">
//                         {filteredItems.map((item) => (
//                             <li key={item.id} className="capitalize">
//                                 <div
//                                     className="flex items-center p-1.5 md:p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600
//                                     cursor-pointer transition-colors"
//                                     onClick={() => handleItemClick(item)}
//                                 >
//                                     <label className="w-full truncate font-medium text-gray-900 dark:text-gray-300">
//                                         {item.title || item.name || item.value}
//                                     </label>
//                                 </div>
//                             </li>
//                         ))}
//                     </ul>
//                 </div>
//             )}
//         </div>
//     );
// };

// export default Dropdown;
